package com.flowyride.feature.planner.ui.components

import com.flowyride.feature.planner.resources.redo
import com.flowyride.feature.planner.resources.undo
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.graphics.StrokeJoin
import androidx.compose.ui.graphics.Path
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.LazyListState
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.ui.draw.drawWithContent
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.BlendMode
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.CompositingStrategy
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.layout.layout
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import com.flowyride.core.designsystem.theme.FlowyColors
import com.flowyride.core.domain.model.Place
import com.flowyride.core.domain.place.SavedPlace
import com.flowyride.core.designsystem.component.SavedPlaceBadge
import com.flowyride.feature.planner.PlannerAction
import com.flowyride.feature.planner.PlannerMode
import com.flowyride.feature.planner.PlannerUiState
import com.flowyride.feature.planner.StopUiState
import com.flowyride.feature.planner.resources.Res
import com.flowyride.feature.planner.resources.arrival_label
import com.flowyride.feature.planner.resources.back
import com.flowyride.feature.planner.resources.cancel
import com.flowyride.feature.planner.resources.clear_field
import com.flowyride.feature.planner.resources.clear_stops
import com.flowyride.feature.planner.resources.clear_stops_message
import com.flowyride.feature.planner.resources.clear_stops_title
import com.flowyride.feature.planner.resources.departure_label
import com.flowyride.feature.planner.resources.favorites_button
import com.flowyride.feature.planner.resources.mode_loop
import com.flowyride.feature.planner.resources.my_position
import com.flowyride.feature.planner.resources.search_destination
import com.flowyride.feature.planner.resources.stops_summary
import org.jetbrains.compose.resources.stringResource

/** Name shown for a stop: "Ma position" when it follows the rider. */
@Composable
internal fun StopUiState.displayName(fallback: String): String = when {
    followsRider -> stringResource(Res.string.my_position)
    selected?.name.isNullOrEmpty() -> fallback
    else -> selected.name
}

/**
 * What floats over the top of the map: the destination search and its shortcuts while nothing is planned,
 * the ride's ends once something is ("Ma position → Grenoble", tap to open the sheet), or the loop's title.
 *
 * @param onDestination a place to ride to was picked (search result or saved address).
 * @param onSummaryClick the ride's ends were tapped: the sheet should open.
 */
@Composable
internal fun PlannerTopBar(
    state: PlannerUiState,
    onAction: (PlannerAction) -> Unit,
    onDestination: (Place) -> Unit,
    onSummaryClick: () -> Unit,
    onOpenFavorites: () -> Unit,
    suggestionsMaxHeight: Dp,
    modifier: Modifier = Modifier,
    accountButton: @Composable () -> Unit = {},
) {
    Column(modifier, verticalArrangement = Arrangement.spacedBy(8.dp)) {
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(4.dp)) {
            Box(Modifier.weight(1f)) {
                when {
                    state.mode == PlannerMode.LOOP && state.imported == null -> LoopBar(onBack = { onAction(PlannerAction.ModeChanged(PlannerMode.ROUTE)) })
                    state.hasRide -> RideSummaryBar(state, onAction, onSummaryClick)
                    else -> DestinationField(state.stops.last(), onAction)
                }
            }
            Surface(shape = RoundedCornerShape(24.dp), color = MaterialTheme.colorScheme.surface, shadowElevation = 4.dp) {
                accountButton()
            }
        }
        val destination = state.stops.last()
        val searching = !state.hasRide && state.mode == PlannerMode.ROUTE
        if (searching && destination.suggestions.isNotEmpty()) {
            Surface(shape = RoundedCornerShape(16.dp), color = MaterialTheme.colorScheme.surface, shadowElevation = 4.dp) {
                Column(Modifier.heightIn(max = suggestionsMaxHeight).verticalScroll(rememberScrollState())) {
                    destination.suggestions.forEachIndexed { index, place ->
                        if (index > 0) HorizontalDivider()
                        SuggestionRow(place, onClick = { onDestination(place) })
                    }
                }
            }
        } else if (searching) {
            ShortcutChips(state.savedPlaces, onAction, onDestination, onOpenFavorites)
        }
    }
}

@Composable
private fun DestinationField(destination: StopUiState, onAction: (PlannerAction) -> Unit) {
    OutlinedTextField(
        value = destination.query,
        onValueChange = { onAction(PlannerAction.QueryChanged(destination.id, it)) },
        placeholder = { Text(stringResource(Res.string.search_destination)) },
        singleLine = true,
        shape = RoundedCornerShape(28.dp),
        leadingIcon = { MarkerDot(FlowyColors.Arrival) },
        trailingIcon = {
            when {
                destination.isSearching -> CircularProgressIndicator(Modifier.size(20.dp), strokeWidth = 2.dp)
                destination.query.isNotEmpty() -> {
                    val clearLabel = stringResource(Res.string.clear_field)
                    IconButton(onClick = { onAction(PlannerAction.FieldCleared(destination.id)) }, modifier = Modifier.semantics { contentDescription = clearLabel }) {
                        Text("✕", style = MaterialTheme.typography.titleMedium)
                    }
                }
            }
        },
        colors = OutlinedTextFieldDefaults.colors(
            focusedContainerColor = MaterialTheme.colorScheme.surface,
            unfocusedContainerColor = MaterialTheme.colorScheme.surface,
        ),
        modifier = Modifier.fillMaxWidth(),
    )
}

/** "Ma position → Grenoble": tap for the details, ✕ to start over. */
@Composable
private fun RideSummaryBar(state: PlannerUiState, onAction: (PlannerAction) -> Unit, onClick: () -> Unit) {
    var confirmClear by remember { mutableStateOf(false) }
    val title = state.imported?.name ?: stringResource(
        Res.string.stops_summary,
        state.stops.first().displayName(stringResource(Res.string.departure_label)),
        state.stops.last().displayName(stringResource(Res.string.arrival_label)),
    )
    BarSurface(Modifier.clickable(onClick = onClick)) {
        Text(
            title,
            Modifier.weight(1f).padding(start = 20.dp),
            style = MaterialTheme.typography.bodyLarge,
            maxLines = 1,
            overflow = TextOverflow.Ellipsis,
        )
        if (state.canUndo || state.canRedo) {
            val undoLabel = stringResource(Res.string.undo)
            IconButton(
                onClick = { onAction(PlannerAction.Undo) },
                enabled = state.canUndo,
                modifier = Modifier.semantics { contentDescription = undoLabel },
            ) { UndoArrow(redo = false, enabled = state.canUndo) }
            val redoLabel = stringResource(Res.string.redo)
            IconButton(
                onClick = { onAction(PlannerAction.Redo) },
                enabled = state.canRedo,
                modifier = Modifier.semantics { contentDescription = redoLabel },
            ) { UndoArrow(redo = true, enabled = state.canRedo) }
        }
        val clearLabel = stringResource(Res.string.clear_stops)
        IconButton(onClick = { confirmClear = true }, modifier = Modifier.semantics { contentDescription = clearLabel }) {
            Text("✕", style = MaterialTheme.typography.titleMedium)
        }
    }
    if (confirmClear) {
        AlertDialog(
            onDismissRequest = { confirmClear = false },
            title = { Text(stringResource(Res.string.clear_stops_title)) },
            text = { Text(stringResource(Res.string.clear_stops_message)) },
            confirmButton = {
                TextButton(onClick = {
                    confirmClear = false
                    onAction(if (state.imported != null) PlannerAction.CloseImported else PlannerAction.ClearStops)
                }) { Text(stringResource(Res.string.clear_stops)) }
            },
            dismissButton = { TextButton(onClick = { confirmClear = false }) { Text(stringResource(Res.string.cancel)) } },
        )
    }
}

@Composable
private fun LoopBar(onBack: () -> Unit) {
    BarSurface {
        val backLabel = stringResource(Res.string.back)
        IconButton(onClick = onBack, modifier = Modifier.semantics { contentDescription = backLabel }) {
            BackChevron()
        }
        Text(stringResource(Res.string.mode_loop), style = MaterialTheme.typography.titleMedium)
    }
}

@Composable
private fun BarSurface(modifier: Modifier = Modifier, content: @Composable RowScope.() -> Unit) {
    Surface(
        shape = RoundedCornerShape(28.dp),
        color = MaterialTheme.colorScheme.surface,
        shadowElevation = 4.dp,
        modifier = Modifier.fillMaxWidth().heightIn(min = 56.dp),
    ) {
        Row(modifier, verticalAlignment = Alignment.CenterVertically) { content() }
    }
}

/** "Boucle", "Mes trajets", then the saved addresses: one line, scrolled sideways. Addresses are managed in the settings. */
@Composable
private fun ShortcutChips(
    places: List<SavedPlace>,
    onAction: (PlannerAction) -> Unit,
    onDestination: (Place) -> Unit,
    onOpenFavorites: () -> Unit,
) {
    val listState = rememberLazyListState()
    LazyRow(
        state = listState,
        horizontalArrangement = Arrangement.spacedBy(6.dp),
        // Room for the chips' shadows, and for the first and last chips to line up with the bar above.
        contentPadding = PaddingValues(horizontal = ChipsBleed, vertical = 4.dp),
        modifier = Modifier
            .bleed(ChipsBleed)
            .fadingEdges(listState),
    ) {
        item {
            Chip(stringResource(Res.string.mode_loop), accent = true, onClick = { onAction(PlannerAction.ModeChanged(PlannerMode.LOOP)) })
        }
        item { Chip(stringResource(Res.string.favorites_button), onClick = onOpenFavorites) }
        items(places, key = { it.id }) { place ->
            Chip(place.name, onClick = { onDestination(place.toPlace()) }, leading = { SavedPlaceBadge(place.emoji, place.color, size = if (place.emoji == null) 14.dp else 22.dp) })
        }
    }
}

/** Lets the row run to the screen edges, past the bar's side margins. */
private fun Modifier.bleed(margin: Dp) = layout { measurable, constraints ->
    val extra = margin.roundToPx()
    val placeable = measurable.measure(
        constraints.copy(minWidth = constraints.minWidth + 2 * extra, maxWidth = constraints.maxWidth + 2 * extra),
    )
    layout((placeable.width - 2 * extra).coerceAtLeast(0), placeable.height) { placeable.place(-extra, 0) }
}

/** Chips fade out on a side where more of them are scrolled away, instead of being cut. */
private fun Modifier.fadingEdges(state: LazyListState) = this
    .graphicsLayer { compositingStrategy = CompositingStrategy.Offscreen }
    .drawWithContent {
        drawContent()
        val fade = ChipsFade.toPx().coerceAtMost(size.width / 2)
        if (state.canScrollBackward) {
            drawRect(
                Brush.horizontalGradient(0f to Color.Transparent, 1f to Color.Black, startX = 0f, endX = fade),
                size = Size(fade, size.height),
                blendMode = BlendMode.DstIn,
            )
        }
        if (state.canScrollForward) {
            drawRect(
                Brush.horizontalGradient(0f to Color.Black, 1f to Color.Transparent, startX = size.width - fade, endX = size.width),
                topLeft = Offset(size.width - fade, 0f),
                size = Size(fade, size.height),
                blendMode = BlendMode.DstIn,
            )
        }
    }

private val ChipsBleed = 12.dp
private val ChipsFade = 32.dp

@Composable
private fun Chip(
    label: String,
    onClick: () -> Unit,
    accent: Boolean = false,
    leading: (@Composable () -> Unit)? = null,
) {
    Surface(
        shape = RoundedCornerShape(18.dp),
        color = if (accent) MaterialTheme.colorScheme.primaryContainer else MaterialTheme.colorScheme.surface,
        contentColor = if (accent) MaterialTheme.colorScheme.onPrimaryContainer else MaterialTheme.colorScheme.onSurface,
        shadowElevation = 3.dp,
    ) {
        Row(
            Modifier
                .clickable(onClick = onClick)
                .padding(horizontal = 14.dp, vertical = 8.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(6.dp),
        ) {
            leading?.invoke()
            Text(label, style = MaterialTheme.typography.labelLarge, maxLines = 1)
        }
    }
}

@Composable
private fun SuggestionRow(place: Place, onClick: () -> Unit) {
    Column(Modifier.fillMaxWidth().clickable(onClick = onClick).padding(horizontal = 16.dp, vertical = 10.dp)) {
        Text(place.name, style = MaterialTheme.typography.bodyLarge, maxLines = 1, overflow = TextOverflow.Ellipsis)
        place.detail?.let {
            Text(it, style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant, maxLines = 1, overflow = TextOverflow.Ellipsis)
        }
    }
}

/** Drawn: arrow glyphs may be missing from the web font. */
/** Drawn, not a glyph: "↶" may be missing from the web font. Mirrored for redo. */
@Composable
private fun UndoArrow(redo: Boolean, enabled: Boolean) {
    val color = MaterialTheme.colorScheme.onSurface.copy(alpha = if (enabled) 1f else 0.38f)
    Canvas(Modifier.size(20.dp).graphicsLayer { scaleX = if (redo) -1f else 1f }) {
        val stroke = 2.dp.toPx()
        val w = size.width
        val h = size.height
        // A hook: from the bottom right, up and round to the left, ending in an arrow head.
        val path = Path().apply {
            moveTo(w * 0.85f, h * 0.85f)
            lineTo(w * 0.85f, h * 0.6f)
            cubicTo(w * 0.85f, h * 0.3f, w * 0.6f, h * 0.3f, w * 0.2f, h * 0.3f)
        }
        drawPath(path, color, style = Stroke(width = stroke, cap = StrokeCap.Round, join = StrokeJoin.Round))
        drawLine(color, Offset(w * 0.2f, h * 0.3f), Offset(w * 0.42f, h * 0.1f), stroke, StrokeCap.Round)
        drawLine(color, Offset(w * 0.2f, h * 0.3f), Offset(w * 0.42f, h * 0.5f), stroke, StrokeCap.Round)
    }
}

@Composable
private fun BackChevron() {
    val color = MaterialTheme.colorScheme.onSurface
    Canvas(Modifier.size(18.dp)) {
        val stroke = 2.5.dp.toPx()
        val top = Offset(size.width * 0.65f, size.height * 0.15f)
        val tip = Offset(size.width * 0.3f, size.height * 0.5f)
        val bottom = Offset(size.width * 0.65f, size.height * 0.85f)
        drawLine(color, top, tip, stroke, StrokeCap.Round)
        drawLine(color, tip, bottom, stroke, StrokeCap.Round)
    }
}
